import { isStepWithSelectors } from '@bugbug/core/types/steps';
import {
  createEmptyCustomSelectorsPreset,
  normalizeSelectorsPreset,
} from '@bugbug/core/utils/selectors';
import { merge, pick } from '@bugbug/core/utils/toolbox';

import type { DefaultParams, DefaultStepsParamsList, GroupedStepsParams } from './steps.types';

import type { Step } from '@bugbug/core/types/steps';

import { DefaultStepSchema, StepSchema } from './steps.schemas';

export const pickDefaultStepParams = <TStepType extends Step['type']>(
  stepType: TStepType,
  stepsParams: GroupedStepsParams,
  matchingParams: DefaultParams = {},
): DefaultParams => {
  const stepsParamsForType = stepsParams[stepType] ?? [];

  let selectedParams;

  if (stepsParamsForType.length === 1) {
    selectedParams = { ...stepsParamsForType[0] };
  } else {
    const matchingStepParams = stepsParamsForType.find((step) =>
      Object.keys(matchingParams).every((key) => step[key] === matchingParams[key]),
    );
    selectedParams = { ...matchingStepParams };
  }

  if ('waitingConditions' in selectedParams && selectedParams.waitingConditions) {
    selectedParams.waitingConditions = selectedParams.waitingConditions.filter(
      (condition) => condition.isGlobal || condition.isActive,
    );
  }

  return selectedParams || {};
};

export const groupDefaultStepsParams = (stepsParams: DefaultStepsParamsList): GroupedStepsParams =>
  stepsParams.reduce((grouped, stepParams) => {
    grouped[stepParams.type] = [...(grouped[stepParams.type] || []), stepParams.params];
    return grouped;
  }, {});

export const getInitialValues = <TStep extends Step>(
  step: Partial<TStep>,
  currentValues: Partial<TStep> = {},
) => {
  const currentType = step.type as TStep['type'];
  const currentStep = {
    ...(merge(step, currentValues) as Partial<TStep>),
    type: step.type,
    isActive: step.isActive,
  };
  const schema = StepSchema[currentType] || DefaultStepSchema;
  const requiredFields = Object.keys(schema.describe().fields);

  const initialValues = pick(requiredFields, currentStep) as Step;

  if (isStepWithSelectors(initialValues)) {
    initialValues.selectorsPresets = initialValues.selectorsPresets?.length
      ? initialValues.selectorsPresets.map((preset) =>
          preset.isCustom ? normalizeSelectorsPreset(preset) : preset,
        )
      : [createEmptyCustomSelectorsPreset()];

    if (initialValues.type === 'dragAndDrop' && initialValues.dndDragOn === 'element') {
      initialValues.dndDropSelectorsPresets = initialValues.dndDropSelectorsPresets?.length
        ? initialValues.dndDropSelectorsPresets.map((preset) =>
            preset.isCustom ? normalizeSelectorsPreset(preset) : preset,
          )
        : [createEmptyCustomSelectorsPreset()];
    }
  }

  return schema.cast(initialValues) as TStep;
};
